/**
 * Session status values.
 */
export const SessionStatus = {
  Active: 0,
  Paused: 1,
  Completed: 2,
  Cancelled: 3,
  Expired: 4,
  Error: 5,
} as const;

export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

/**
 * Message role values.
 */
export const MessageRole = {
  System: 0,
  User: 1,
  Assistant: 2,
  Tool: 3,
} as const;

export type MessageRole = (typeof MessageRole)[keyof typeof MessageRole];

/**
 * Agent session data transfer object.
 */
export interface AgentSessionDto {
  id: string;
  agentDefinitionId: string;
  agentName: string;
  sessionName: string;
  status: SessionStatus;
  messageCount: number;
  totalTokensUsed: number;
  totalCost: number;
  createdAt: string;
  lastActivityAt?: string;
  tenantId: string;
  projectId?: string;
  initialParametersJson?: string;
  hasContextSummaries?: boolean;
  contextSummaryCount?: number;
  /** Current harness operating mode ("execute" | "plan" | "auto"); null for classic sessions. */
  currentHarnessMode?: string | null;
  /** JSON snapshot of the harness todo list as of the last completed harness turn. */
  harnessTodosJson?: string | null;
  /** Declared memory scope (dimension key/value JSON) stored on the session. */
  memoryDeclaredScopeJson?: string | null;
  /** How the memory enqueue was disposed of on Complete: Queued | Duplicate | Ignored | SkippedScope | Error. */
  memoryQueueStatus?: string | null;
  /** Why nothing was enqueued, when memoryQueueStatus says so. */
  memoryQueueReason?: string | null;
}

/**
 * Harness operating modes for agents with the Microsoft Agent Framework harness enabled.
 */
export const HarnessMode = {
  Execute: 'execute',
  Plan: 'plan',
  Auto: 'auto',
} as const;

export type HarnessMode = (typeof HarnessMode)[keyof typeof HarnessMode];

/**
 * One harness todo item (parsed from harnessTodosJson).
 */
export interface HarnessTodoItem {
  id: number;
  title: string;
  description?: string;
  isComplete: boolean;
}

/**
 * One tool call persisted on an assistant message (parsed from toolCallsJson).
 * Harness turns store name + success only; classic turns store name + arguments (no success).
 */
export interface PersistedToolCall {
  id: string;
  name: string;
  arguments?: string;
  /** Present on harness-path records; undefined for classic-path records. */
  success?: boolean;
}

/**
 * Parses an AgentMessageDto.toolCallsJson payload.
 * Tolerates backend PascalCase (Id/Name/Arguments/Success) and camelCase keys;
 * returns an empty array for missing or invalid JSON.
 */
export const parseToolCalls = (toolCallsJson?: string | null): PersistedToolCall[] => {
  if (!toolCallsJson) return [];
  try {
    const raw = JSON.parse(toolCallsJson);
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((item) => item && typeof item === 'object')
      .map((item: Record<string, unknown>) => ({
        id: String(item.Id ?? item.id ?? ''),
        name: String(item.Name ?? item.name ?? ''),
        arguments:
          typeof item.Arguments === 'string'
            ? item.Arguments
            : typeof item.arguments === 'string'
              ? item.arguments
              : undefined,
        success:
          typeof item.Success === 'boolean'
            ? item.Success
            : typeof item.success === 'boolean'
              ? item.success
              : undefined,
      }))
      .filter((call) => call.name.length > 0);
  } catch {
    return [];
  }
};

/**
 * Agent message data transfer object.
 */
export interface AgentMessageDto {
  id: string;
  sessionId: string;
  role: MessageRole;
  content: string;
  sequenceNumber: number;
  toolCallsJson?: string;
  toolResultsJson?: string;
  mediaType?: string | null;
  fileId?: string | null;
  mediaMetadataJson?: string | null;
  processedContentJson?: string | null;
  tokensUsed?: number;
  cost?: number;
  processedAt?: string;
  createdAt: string;
}

/**
 * Request model for creating a session.
 */
export interface CreateSessionRequest {
  agentDefinitionId: string;
  /**
   * Optional friendly name. When omitted, the backend defaults the
   * session name to the newly generated session identifier.
   */
  sessionName?: string;
  parameters?: Record<string, any>;
  initialMessage?: string;
}

/**
 * Request model for updating a session.
 */
export interface UpdateSessionRequest {
  sessionName?: string;
}

/**
 * Request model for sending a message.
 */
export interface SendMessageRequest {
  content: string;
  fileId?: string;
  mediaType?: string;
  mediaMetadataJson?: string;
  language?: string;
  /**
   * Harness operating mode for this turn ("execute" | "plan" | "auto").
   * Omitted defaults to "execute". Only honored for harness-enabled agents.
   */
  harnessMode?: string;
}

/**
 * Filters for session queries.
 */
export interface SessionFilters {
  pageNumber?: number;
  pageSize?: number;
  agentDefinitionId?: string;
  status?: SessionStatus;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
}
